import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Calendar, MapPin, ChevronRight, Loader2 } from 'lucide-react';
import { getAnonymousId, trackRecommendationClick } from '@/hooks/useTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "More Events You Might Like" for city pages
 * Shows personalized recommendations excluding events already visible on the page
 * 
 * @param {Object} props
 * @param {string} props.citySlug - Current city slug
 * @param {string} props.cityName - City display name
 * @param {number} props.limit - Number of recommendations
 */
export default function YouMightLikeCity({ citySlug, cityName, limit = 4 }) {
  const [recommendations, setRecommendations] = useState([]);
  const [profileSummary, setProfileSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const anonId = getAnonymousId();
        
        // Use the main recommendations endpoint with city context
        const response = await axios.post(
          `${BACKEND_URL}/api/ai/recommendations`,
          {
            context: {
              type: 'city',
              citySlug: citySlug
            },
            limit: limit + 2, // Request more to filter out same-city events
            excludeViewed: false
          },
          {
            headers: {
              'X-Anonymous-ID': anonId
            }
          }
        );
        
        // Filter to show events from OTHER cities (to encourage exploration)
        // But keep some from same city if user has strong preferences there
        const allRecs = response.data.recommendations || [];
        const otherCityRecs = allRecs.filter(r => r.citySlug !== citySlug);
        const sameCityRecs = allRecs.filter(r => r.citySlug === citySlug);
        
        // Prioritize other cities, but include same city if not enough variety
        let finalRecs = otherCityRecs.slice(0, limit);
        if (finalRecs.length < limit) {
          finalRecs = [...finalRecs, ...sameCityRecs.slice(0, limit - finalRecs.length)];
        }
        
        setRecommendations(finalRecs.slice(0, limit));
        setProfileSummary(response.data.profileSummary);
        setError(false);
      } catch (err) {
        console.error('Failed to fetch city recommendations:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    if (citySlug) {
      fetchRecommendations();
    }
  }, [citySlug, limit]);

  const handleClick = (recommendation) => {
    trackRecommendationClick(recommendation, 'city_page');
  };

  // Don't render if no recommendations or error
  if (error || (!loading && recommendations.length === 0)) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6">
      <div className="flex items-center mb-2">
        <Sparkles className="w-6 h-6 text-purple-600 mr-2" />
        <h3 className="text-xl font-bold text-gray-800">
          More Events You Might Like
        </h3>
      </div>
      
      {profileSummary?.topSectors?.length > 0 ? (
        <p className="text-gray-600 mb-6">
          Based on your interest in{' '}
          <span className="font-medium">{profileSummary.topSectors.slice(0, 2).join(' and ')}</span>
          {' '}— explore events beyond {cityName}
        </p>
      ) : (
        <p className="text-gray-600 mb-6">
          Discover more events across the UK
        </p>
      )}
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {recommendations.map((rec) => (
              <Link
                key={rec.eventId}
                to={rec.url}
                onClick={() => handleClick(rec)}
                className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-lg transition-all group"
              >
                <h4 className="font-semibold text-gray-900 group-hover:text-purple-700 mb-2 line-clamp-2">
                  {rec.eventName}
                </h4>
                
                <div className="space-y-1 mb-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{rec.venueName}, {rec.city}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                    <span>{rec.dates?.display || 'Dates TBC'}</span>
                  </div>
                </div>
                
                {rec.sectorTags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {rec.sectorTags.slice(0, 2).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                
                <p className="text-sm text-purple-600 font-medium flex items-center">
                  {rec.reason}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </p>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-6">
            <Link 
              to="/events"
              className="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
            >
              Browse All UK Events
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
